import { appointmentDateParts } from '@/lib/format';
import { MapPin, Video, Check, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScheduleAppointment } from '@vagusx/clients';
import type { ProviderColor } from './hooks/use-provider-colors';
import {
  APPOINTMENT_STATUS,
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
} from './hooks/use-schedule';

interface AppointmentCardProps {
  appointment: ScheduleAppointment;
  providerColor?: ProviderColor;
  onClick?: () => void;
  compact?: boolean;
}

export function AppointmentCard({
  appointment,
  providerColor,
  onClick,
  compact = false,
}: AppointmentCardProps): React.ReactElement {
  // The clinic's clock, matching the day header (grouped by the clinic's day).
  const tz = appointment.location?.timezone;
  const start = appointmentDateParts(appointment.startTime, tz);
  const end = appointmentDateParts(appointment.endTime, tz);
  const statusColors = APPOINTMENT_STATUS_COLORS[appointment.status] ?? {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
  };
  const statusLabel = APPOINTMENT_STATUS_LABELS[appointment.status] ?? 'Unknown';

  const patientName = appointment.patient
    ? `${appointment.patient.firstName} ${appointment.patient.lastName}`
    : 'Unknown Patient';

  const providerName = appointment.provider
    ? `${appointment.provider.lastName}`
    : 'Unknown Provider';

  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative rounded-md border border-border/60 transition-all cursor-pointer overflow-hidden',
        'hover:border-border hover:shadow-xs',
        providerColor?.bg ?? 'bg-card',
        providerColor?.border ? `border-l-[3px] ${providerColor.border}` : ''
      )}
    >
      <div className={cn('p-2.5', compact && 'p-2')}>
        {/* Time and Status Row */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span className="font-medium">
              {start.time} - {end.time}
              {start.zoneLabel && ` ${start.zoneLabel}`}
            </span>
          </div>
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded',
              statusColors.bg,
              statusColors.text
            )}
          >
            {appointment.status === APPOINTMENT_STATUS.BOOKED && <Check className="h-2.5 w-2.5" />}
            {statusLabel}
          </span>
        </div>

        {/* Patient Name */}
        <div className="font-medium text-sm text-foreground truncate mb-1">{patientName}</div>

        {/* Appointment Type / Reason */}
        {(appointment.appointmentType?.name || appointment.reasonForVisit) && !compact && (
          <div className="text-xs text-muted-foreground truncate mb-1.5">
            {appointment.appointmentType?.name ?? appointment.reasonForVisit}
          </div>
        )}

        {/* Provider and Location Row */}
        <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className={cn('font-medium truncate', providerColor?.text)}>{providerName}</span>
          <div className="flex items-center gap-1 shrink-0">
            {appointment.isTelehealth ? (
              <>
                <Video className="h-3 w-3 text-blue-500 dark:text-blue-400" />
                <span>Telehealth</span>
              </>
            ) : appointment.location ? (
              <>
                <MapPin className="h-3 w-3" />
                <span className="truncate max-w-[100px]">{appointment.location.name}</span>
              </>
            ) : null}
          </div>
        </div>

        {/* Confirmation Number - only show if not compact */}
        {appointment.confirmationNumber && !compact && (
          <div className="mt-1.5 pt-1.5 border-t border-border/50 text-[10px] text-muted-foreground/70 truncate">
            Conf: {appointment.confirmationNumber}
          </div>
        )}
      </div>
    </div>
  );
}
